/**
 * Selectable Encompass loan-role contract.
 *
 * These IDs are the tenant's Encompass Role Settings IDs, confirmed against
 * the V3 settings/roles API contract. System Role 8 is intentionally absent.
 * Assignment sources are canonical projections owned by the loan sync paths;
 * callers must not substitute the broad V1 /associates response.
 */

export type EncompassRoleAssignmentSource =
  | 'designated'
  | 'fixed_milestone'
  | 'milestone_free'
  | 'milestone';

export interface SupportedEncompassRole {
  roleId: number;
  roleName: string;
  abbreviation: string;
  assignmentSource: EncompassRoleAssignmentSource;
  legacySlug:
    | 'loan_officer'
    | 'loan_officer_2'
    | 'processor'
    | 'loan_officer_assistant'
    | 'closer'
    | 'jr_processor'
    | null;
  aliases: readonly string[];
}

export interface EncompassRoleCandidate {
  encompass_user_id: string | null;
  email: string | null;
  name: string | null;
  source_log_id: string | null;
}

export interface EncompassRoleObservation {
  role_id: number;
  candidates: EncompassRoleCandidate[];
}

export const SUPPORTED_ENCOMPASS_ROLES = [
  {
    roleId: 0,
    roleName: 'File Starter',
    abbreviation: 'FS',
    assignmentSource: 'fixed_milestone',
    legacySlug: null,
    aliases: ['File Starter']
  },
  {
    roleId: 1,
    roleName: 'Loan Officer',
    abbreviation: 'LO',
    assignmentSource: 'designated',
    legacySlug: 'loan_officer',
    aliases: ['Loan Officer']
  },
  {
    roleId: 5,
    roleName: 'Loan Processor',
    abbreviation: 'LP',
    assignmentSource: 'designated',
    legacySlug: 'processor',
    aliases: ['Loan Processor', 'Processor']
  },
  {
    roleId: 6,
    roleName: 'Underwriter',
    abbreviation: 'UW',
    assignmentSource: 'designated',
    legacySlug: null,
    aliases: ['Underwriter', 'Loan Underwriter']
  },
  {
    roleId: 7,
    roleName: 'Closer',
    abbreviation: 'CL',
    assignmentSource: 'designated',
    legacySlug: 'closer',
    aliases: ['Closer']
  },
  {
    roleId: 9,
    roleName: 'Post Closer',
    abbreviation: 'PC',
    assignmentSource: 'milestone',
    legacySlug: null,
    aliases: ['Post Closer', 'Post-Closer']
  },
  {
    roleId: 14,
    roleName: 'Accounting',
    abbreviation: 'Accounting',
    assignmentSource: 'milestone_free',
    legacySlug: null,
    aliases: ['Accounting']
  },
  {
    roleId: 20,
    roleName: 'Branch Manager',
    abbreviation: 'BM',
    assignmentSource: 'milestone_free',
    legacySlug: null,
    aliases: ['Branch Manager']
  },
  {
    roleId: 21,
    roleName: 'Compliance',
    abbreviation: 'Compliance',
    assignmentSource: 'milestone_free',
    legacySlug: null,
    aliases: ['Compliance']
  },
  {
    roleId: 22,
    roleName: 'Loan Officer 2',
    abbreviation: 'LO2',
    assignmentSource: 'milestone_free',
    legacySlug: 'loan_officer_2',
    aliases: ['Loan Officer 2']
  },
  {
    roleId: 23,
    roleName: 'Loan Officer Assistant',
    abbreviation: 'LOA',
    assignmentSource: 'milestone_free',
    legacySlug: 'loan_officer_assistant',
    aliases: [
      'Loan Officer Assistant',
      'Loan Officer Assist',
      'LO Assistant'
    ]
  },
  {
    roleId: 24,
    roleName: 'Junior Loan Processor',
    abbreviation: 'Jr LP',
    assignmentSource: 'milestone_free',
    legacySlug: 'jr_processor',
    aliases: [
      'Junior Loan Processor',
      'Jr. Loan Processor',
      'Jr Loan Processor'
    ]
  },
  {
    roleId: 26,
    roleName: 'Insuring Specialist',
    abbreviation: 'Insuring',
    assignmentSource: 'milestone_free',
    legacySlug: null,
    aliases: ['Insuring Specialist']
  },
  {
    roleId: 27,
    roleName: 'Servicer',
    abbreviation: 'Servicer',
    assignmentSource: 'milestone_free',
    legacySlug: null,
    aliases: ['Servicer']
  },
  {
    roleId: 28,
    roleName: 'Lock Desk Analyst',
    abbreviation: 'Lock Desk',
    assignmentSource: 'milestone_free',
    legacySlug: null,
    aliases: ['Lock Desk Analyst']
  },
  {
    roleId: 37,
    roleName: 'Department Manager',
    abbreviation: 'Dept Mgr',
    assignmentSource: 'milestone_free',
    legacySlug: null,
    aliases: ['Department Manager', 'Dept Manager']
  }
] as const satisfies readonly SupportedEncompassRole[];

export const SUPPORTED_ENCOMPASS_ROLE_IDS = new Set<number>(
  SUPPORTED_ENCOMPASS_ROLES.map((role) => role.roleId)
);

function normalizeRoleName(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[.-]/g, ' ')
    .replace(/\s+/g, ' ');
}

export function supportedEncompassRoleById(
  roleId: number
): SupportedEncompassRole | null {
  return SUPPORTED_ENCOMPASS_ROLES.find((role) => role.roleId === roleId) ?? null;
}

export function supportedEncompassRoleByName(
  roleName: string
): SupportedEncompassRole | null {
  const normalized = normalizeRoleName(roleName);
  return SUPPORTED_ENCOMPASS_ROLES.find((role) =>
    role.aliases.some((alias) => normalizeRoleName(alias) === normalized)
  ) ?? null;
}

export function supportedEncompassRoleForLegacySlug(
  legacySlug: string
): SupportedEncompassRole | null {
  return SUPPORTED_ENCOMPASS_ROLES.find(
    (role) => role.legacySlug === legacySlug
  ) ?? null;
}
